import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Meal Plan master (Masters → Meal Plans). Typed split out of legacy
 * `tbl_MiscellaneousMaster` where `FieldFilter = 'MealType'`. Common codes:
 * EP (no meals), CP (breakfast), MAP / HB (breakfast + dinner), AP / FB (all
 * meals), AI (all-inclusive). Linked from `itinerary_components` for HOTEL
 * components. Standard master shape per ADR-0016.
 */

export const MealPlanListItemSchema = z.object({
  id: UuidSchema,
  legacyLookupId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type MealPlanListItem = z.infer<typeof MealPlanListItemSchema>;

export const MealPlanListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type MealPlanListQuery = z.infer<typeof MealPlanListQuerySchema>;

export const MealPlanListResponseSchema = paginatedResponseSchema(MealPlanListItemSchema);
export type MealPlanListResponse = z.infer<typeof MealPlanListResponseSchema>;

export const MealPlanDetailSchema = MealPlanListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type MealPlanDetail = z.infer<typeof MealPlanDetailSchema>;

const MealPlanCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyLookupId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateMealPlanRequestSchema = MealPlanCoreFieldsSchema;
export type CreateMealPlanRequest = z.infer<typeof CreateMealPlanRequestSchema>;

export const UpdateMealPlanRequestSchema = MealPlanCoreFieldsSchema.partial();
export type UpdateMealPlanRequest = z.infer<typeof UpdateMealPlanRequestSchema>;
